import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Computer as ComputerIcon,
  Description as DescriptionIcon,
  Inventory as InventoryIcon,
  Key as KeyIcon,
  Notifications as NotificationsIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import { useAuth } from '../context/AuthContext';
import { assetService, auditService } from '../services/api';
import Layout from '../components/Layout/Layout';
import AssetTypeSelectorModal from '../components/common/AssetTypeSelectorModal';

const StatCard = ({ title, value, icon, color, subtitle, tooltip }) => (
  <Tooltip title={tooltip || ''} arrow disableHoverListener={!tooltip}>
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 46,
              height: 46,
              bgcolor: `${color}.main`,
              color: `${color}.contrastText`,
              boxShadow: (theme) => theme.shadows[2],
            }}
          >
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ lineHeight: 1.05 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
          </Box>
        </Box>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  </Tooltip>
);

const CategoryCard = ({ category, count, total, icon, color }) => {
  const percentage = Math.round((count / Math.max(total, 1)) * 100);

  return (
    <Paper sx={{ p: 1.8, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main`, width: 38, height: 38 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {category}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {count} ativos
            </Typography>
          </Box>
        </Box>
        <Chip label={`${percentage}%`} size="small" color={color} variant="outlined" />
      </Box>
      <LinearProgress variant="determinate" value={percentage} sx={{ mt: 1.5, height: 8 }} color={color} />
    </Paper>
  );
};

const getActivityLabel = (item) => {
  const action = item.action || 'action';
  const resource = item.resource_type || 'resource';
  const idPart = item.resource_id ? ` #${item.resource_id}` : '';

  const actionMap = {
    create: 'criado',
    update: 'atualizado',
    delete: 'removido',
    login: 'login',
    asset_created: 'criado',
    asset_updated: 'atualizado',
    asset_deleted: 'removido',
    asset_transferred: 'transferido',
    asset_permission_updated: 'permissões atualizadas',
    inventory_item_created: 'item criado',
    inventory_item_updated: 'item atualizado',
    inventory_item_deleted: 'item removido',
    inventory_movement_created: 'movimentação criada',
    inventory_movement_updated: 'movimentação atualizada',
    inventory_transfer_created: 'transferência criada',
    inventory_transfer_updated: 'transferência atualizada',
    user_created: 'criado',
    user_updated: 'atualizado',
    user_deleted: 'removido',
    user_status_toggled: 'status alterado',
    user_group_created: 'grupo criado',
    user_group_updated: 'grupo atualizado',
    user_group_deleted: 'grupo removido',
    alert_rule_created: 'regra de alerta criada',
    alert_rule_updated: 'regra de alerta atualizada',
    alert_rule_deleted: 'regra de alerta removida',
    report_created: 'relatório criado',
    report_updated: 'relatório atualizado',
    report_deleted: 'relatório removido',
  };

  return `${resource}${idPart} ${actionMap[action] || action}`;
};

const getActivityIcon = (resourceType) => {
  switch (resourceType) {
    case 'asset':
      return <InventoryIcon fontSize="small" />;
    case 'user':
    case 'user_group':
      return <PeopleIcon fontSize="small" />;
    case 'inventory':
    case 'transaction':
    case 'license':
      return <AssignmentIcon fontSize="small" />;
    case 'certificate':
      return <KeyIcon fontSize="small" />;
    case 'domain':
      return <DescriptionIcon fontSize="small" />;
    case 'alert_rule':
      return <NotificationsIcon fontSize="small" />;
    case 'report':
      return <DescriptionIcon fontSize="small" />;
    default:
      return <AccessTimeIcon fontSize="small" />;
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assetTypeModalOpen, setAssetTypeModalOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const statsResponse = await assetService.getStats();
        setStats(statsResponse);

        if (user?.role === 'admin') {
          const recentResponse = await auditService.getRecentActivities(10);
          setActivities(recentResponse.activities || []);
        }
      } catch (error) {
        setStats(null);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, user?.role]);

  const categoryData = stats?.by_category || [];
  const totalAssets = useMemo(
    () => categoryData.reduce((sum, category) => sum + category.count, 0),
    [categoryData]
  );

  const expiringBreakdown = stats?.expiring_breakdown || {};
  const expiringTooltip = `Ativos: ${expiringBreakdown.asset || 0} | Certificados: ${expiringBreakdown.certificate || 0} | Licenças: ${expiringBreakdown.license || 0} | Domínios: ${expiringBreakdown.domain || 0}`;
  const canManage = user?.role === 'admin' || user?.role === 'manager';

  const handleAssetTypeSelection = (type) => {
    const routeByType = {
      physical: '/assets/physical/new',
      token: '/assets/token/new',
      domain: '/domains/new',
      certificate: '/certificates/new',
      license: '/licenses/new',
    };

    setAssetTypeModalOpen(false);
    navigate(routeByType[type] || '/assets/physical/new');
  };

  const categoryConfig = (categoryKey) => {
    switch (categoryKey) {
      case 'physical':
        return { icon: <ComputerIcon fontSize="small" />, color: 'primary' };
      case 'token':
        return { icon: <KeyIcon fontSize="small" />, color: 'secondary' };
      case 'license':
        return { icon: <AssignmentIcon fontSize="small" />, color: 'warning' };
      case 'certificate':
        return { icon: <KeyIcon fontSize="small" />, color: 'info' };
      case 'domain':
        return { icon: <DescriptionIcon fontSize="small" />, color: 'secondary' };
      case 'homologated_device':
        return { icon: <ComputerIcon fontSize="small" />, color: 'success' };
      default:
        return { icon: <InventoryIcon fontSize="small" />, color: 'primary' };
    }
  };

  return (
    <Layout>
      <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, maxWidth: 1280, mx: 'auto', width: '100%' }}>
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            mb: 2.5,
            border: 'none',
            color: 'common.white',
            background: 'linear-gradient(130deg, #1f5eff 0%, #00a387 100%)',
          }}
        >
          <Typography variant="h4" sx={{ mb: 0.8 }}>
            Dashboard
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.95 }}>
            Bem-vindo ao Sistema de Gestão de Inventário, {user?.name || 'usuário'}.
          </Typography>
        </Paper>

        {loading ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Carregando indicadores...
            </Typography>
            <LinearProgress />
          </Paper>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Total de Ativos"
                  value={stats?.summary?.total || 0}
                  icon={<InventoryIcon fontSize="small" />}
                  color="primary"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Ativos em Estoque"
                  value={stats?.summary?.active || 0}
                  icon={<CheckCircleIcon fontSize="small" />}
                  color="success"
                  subtitle={`${Math.round(((stats?.summary?.active || 0) / Math.max(stats?.summary?.total || 1, 1)) * 100)}% do total`}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Expirando em Breve"
                  value={stats?.summary?.expiring_soon || 0}
                  icon={<WarningIcon fontSize="small" />}
                  color="warning"
                  tooltip={expiringTooltip}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Usuários Ativos"
                  value={stats?.summary?.active_users_total || 0}
                  icon={<PeopleIcon fontSize="small" />}
                  color="info"
                  subtitle={`${stats?.summary?.active_users_recent_7d || 0} logados nos últimos 7 dias`}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} lg={8}>
                <Paper sx={{ p: 2.2, height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 1.2 }}>
                    Distribuição por Categoria
                  </Typography>
                  <Grid container spacing={1.5}>
                    {categoryData.map((category) => {
                      const config = categoryConfig(category.category);
                      return (
                        <Grid item xs={12} md={6} key={category.category}>
                          <CategoryCard
                            category={category.category}
                            count={category.count}
                            total={totalAssets}
                            icon={config.icon}
                            color={config.color}
                          />
                        </Grid>
                      );
                    })}
                    {categoryData.length === 0 && (
                      <Grid item xs={12}>
                        <Alert severity="info">Nenhuma categoria disponível para exibição.</Alert>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {user?.role === 'admin' && (
                <Grid item xs={12} lg={4}>
                  <Paper sx={{ p: 2.2, height: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 1.2 }}>
                      Atividades Recentes
                    </Typography>
                    <List disablePadding>
                      {activities.map((item, index) => (
                        <React.Fragment key={item.id}>
                          <ListItem disableGutters>
                            <ListItemAvatar>
                              <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.light', color: 'primary.main' }}>
                                {getActivityIcon(item.resource_type)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primaryTypographyProps={{ fontSize: '0.86rem', fontWeight: 700 }}
                              secondaryTypographyProps={{ fontSize: '0.75rem' }}
                              primary={getActivityLabel(item)}
                              secondary={new Date(item.created_at).toLocaleString('pt-BR')}
                            />
                            <Chip
                              size="small"
                              label={item.status === 'success' ? 'Concluído' : 'Falha'}
                              color={item.status === 'success' ? 'success' : 'error'}
                            />
                          </ListItem>
                          {index < activities.length - 1 && <Divider component="li" />}
                        </React.Fragment>
                      ))}
                    </List>

                    {activities.length === 0 && (
                      <Alert severity="info" sx={{ mb: 1.5 }}>
                        Nenhuma atividade registrada.
                      </Alert>
                    )}

                    <Button variant="outlined" fullWidth onClick={() => navigate('/audit')}>
                      Ver Todas as Atividades
                    </Button>
                  </Paper>
                </Grid>
              )}
            </Grid>

            <Paper sx={{ p: 2.2, mt: 2.5 }}>
              <Typography variant="h6" sx={{ mb: 1.4 }}>
                Ações Rápidas
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} lg={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<InventoryIcon fontSize="small" />}
                    sx={{ py: 1.3 }}
                    onClick={() => setAssetTypeModalOpen(true)}
                  >
                    Novo Ativo
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PeopleIcon fontSize="small" />}
                    sx={{ py: 1.3 }}
                    onClick={() => navigate('/users')}
                    disabled={!canManage}
                  >
                    Gerenciar Usuários
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DescriptionIcon fontSize="small" />}
                    sx={{ py: 1.3 }}
                    onClick={() => navigate('/reports')}
                    disabled={!canManage}
                  >
                    Relatórios
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<NotificationsIcon fontSize="small" />}
                    sx={{ py: 1.3 }}
                    onClick={() => navigate('/alerts')}
                    disabled={!canManage}
                  >
                    Alertas
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}

        <AssetTypeSelectorModal
          open={assetTypeModalOpen}
          onClose={() => setAssetTypeModalOpen(false)}
          onSelect={handleAssetTypeSelection}
        />
      </Box>
    </Layout>
  );
};

export default Dashboard;
